import { Checkbox } from '@trustoo/components/Checkbox/v2'
import { Radio } from '@trustoo/components/Radio/v2'
import clsx from 'clsx'
import { FC, ReactNode, useId, useState } from 'react'

export type RadioGroupSize = 'sm' | 'md'
export type RadioGroupType =
    | 'radio-button'
    | 'checkbox'
    | 'icon-simple'
    | 'icon-card'
    | 'avatar'
    | 'payment-icon'

export interface RadioGroupItem {
    value: string
    title: ReactNode
    subtext?: ReactNode
    supportingText?: ReactNode
    leading?: ReactNode
    badge?: ReactNode
    actions?: ReactNode
    disabled?: boolean
}

export interface RadioGroupProps {
    items: RadioGroupItem[]
    size?: RadioGroupSize
    type?: RadioGroupType
    multiple?: boolean
    value?: string | string[]
    defaultValue?: string | string[]
    onChange?: (value: string | string[]) => void
    name?: string
    disabled?: boolean
    className?: string
}

type Layout = 'leading-control' | 'trailing-control' | 'card'

interface TypeConfig {
    layout: Layout
    leading: Record<RadioGroupSize, string> | null
}

const TYPE_CONFIG: Record<RadioGroupType, TypeConfig> = {
    'radio-button': {
        layout: 'leading-control',
        leading: null,
    },
    checkbox: {
        layout: 'leading-control',
        leading: null,
    },
    'icon-simple': {
        layout: 'trailing-control',
        leading: {
            sm: 'size-8 rounded-radius-sm border border-solid border-border-primary bg-background-bg-primary text-foreground-fg-secondary shadow-xs-skeuomorphic [&_svg]:size-4',
            md: 'size-10 rounded-radius-md border border-solid border-border-primary bg-background-bg-primary text-foreground-fg-secondary shadow-xs-skeuomorphic [&_svg]:size-5',
        },
    },
    'icon-card': {
        layout: 'card',
        leading: {
            sm: 'size-8 rounded-radius-sm border border-solid border-border-primary bg-background-bg-primary text-foreground-fg-secondary shadow-xs-skeuomorphic [&_svg]:size-4',
            md: 'size-10 rounded-radius-md border border-solid border-border-primary bg-background-bg-primary text-foreground-fg-secondary shadow-xs-skeuomorphic [&_svg]:size-5',
        },
    },
    avatar: {
        layout: 'trailing-control',
        leading: {
            sm: 'size-8 overflow-hidden rounded-radius-full bg-background-bg-tertiary',
            md: 'size-10 overflow-hidden rounded-radius-full bg-background-bg-tertiary',
        },
    },
    'payment-icon': {
        layout: 'trailing-control',
        leading: {
            sm: 'h-8 w-[46px] overflow-hidden rounded-radius-xs border border-solid border-border-secondary bg-background-bg-primary',
            md: 'h-8 w-[46px] overflow-hidden rounded-radius-xs border border-solid border-border-secondary bg-background-bg-primary',
        },
    },
}

interface SizeClasses {
    text: string
    contentGap: string
    textColumn: string
    titleRow: string
    cardTitle: string
    price: string
    periodBody: string
}

const SIZE_CLASSES: Record<RadioGroupSize, SizeClasses> = {
    sm: {
        text: 'text-sm',
        contentGap: 'gap-spacing-md',
        textColumn: 'gap-spacing-none',
        titleRow: 'gap-spacing-xs',
        cardTitle: 'text-md',
        price: 'text-display-sm [&_small]:text-sm',
        periodBody: 'text-sm',
    },
    md: {
        text: 'text-md',
        contentGap: 'gap-spacing-lg',
        textColumn: 'gap-spacing-xxs',
        titleRow: 'gap-spacing-sm',
        cardTitle: 'text-lg',
        price: 'text-display-md [&_small]:text-md',
        periodBody: 'text-md',
    },
}

const renderBadge = (badge: ReactNode) => {
    if (typeof badge === 'string' || typeof badge === 'number') {
        return (
            <span className="inline-flex items-center gap-spacing-xs rounded-radius-sm border border-solid border-border-primary bg-background-bg-primary px-spacing-sm py-spacing-xxs text-xs font-medium text-text-secondary shadow-xs">
                <span className="flex size-2 items-center justify-center" aria-hidden="true">
                    <span className="size-1.5 rounded-radius-full bg-foreground-fg-success-secondary" />
                </span>
                {badge}
            </span>
        )
    }
    return badge
}

export const RadioGroup: FC<RadioGroupProps> = ({
    items,
    size = 'md',
    type = 'radio-button',
    multiple = false,
    value,
    defaultValue,
    onChange,
    name: providedName,
    disabled = false,
    className,
}) => {
    const generatedId = useId()
    const name = providedName || generatedId
    const sizeClasses = SIZE_CLASSES[size]
    const typeConfig = TYPE_CONFIG[type]

    const [internalValue, setInternalValue] = useState<string | string[]>(() => {
        if (multiple) {
            return Array.isArray(defaultValue) ? defaultValue : []
        }
        return typeof defaultValue === 'string' ? defaultValue : ''
    })

    const isControlled = value !== undefined
    const currentValue = isControlled ? value : internalValue

    const isSelected = (item: RadioGroupItem): boolean => {
        if (multiple) {
            return Array.isArray(currentValue) && currentValue.includes(item.value)
        }
        return currentValue === item.value
    }

    const handleItemChange = (item: RadioGroupItem, checked: boolean) => {
        if (disabled || item.disabled) {
            return
        }

        let nextValue: string | string[]

        if (multiple) {
            const currentArray = Array.isArray(currentValue) ? currentValue : []
            if (checked) {
                nextValue = [...currentArray, item.value]
            } else {
                nextValue = currentArray.filter((entry) => entry !== item.value)
            }
        } else {
            nextValue = checked ? item.value : ''
        }

        if (!isControlled) {
            setInternalValue(nextValue)
        }
        onChange?.(nextValue)
    }

    const WrapperComponent = multiple ? 'fieldset' : 'div'
    const wrapperRole = multiple ? 'group' : 'radiogroup'

    return (
        <WrapperComponent
            role={wrapperRole}
            className={clsx(
                'm-spacing-none flex w-full min-w-0 flex-col gap-spacing-lg border-0 p-spacing-none font-body',
                className,
            )}>
            {items.map((item) => {
                const itemId = `${generatedId}-${item.value}`
                const selected = isSelected(item)
                const itemDisabled = disabled || Boolean(item.disabled)
                const controlName = multiple ? `${name}-${item.value}` : name
                const labelCursor = itemDisabled ? 'cursor-not-allowed' : 'cursor-pointer'

                const controlProps = {
                    size,
                    checked: selected,
                    disabled: itemDisabled,
                    id: itemId,
                    name: controlName,
                    value: item.value,
                    onChange: (checked: boolean) => handleItemChange(item, checked),
                }

                const control =
                    type === 'checkbox' ? (
                        <Checkbox {...controlProps} />
                    ) : (
                        <Radio {...controlProps} />
                    )

                const leading =
                    typeConfig.leading && item.leading ? (
                        <label
                            htmlFor={itemId}
                            className={clsx(
                                'flex shrink-0 items-center justify-center',
                                labelCursor,
                                typeConfig.leading[size],
                            )}>
                            {item.leading}
                        </label>
                    ) : null

                const cardClasses = clsx(
                    'relative flex w-full rounded-radius-xl border border-solid transition-colors',
                    selected
                        ? 'border-border-brand ring-1 ring-inset ring-border-brand'
                        : 'border-border-secondary',
                    itemDisabled ? 'bg-background-bg-secondary' : 'bg-background-bg-primary',
                    typeConfig.layout === 'card' ? 'flex-col' : 'flex-row items-start p-spacing-xl',
                    typeConfig.layout === 'trailing-control' && 'gap-spacing-xs',
                )

                if (typeConfig.layout === 'card') {
                    return (
                        <div key={item.value} className={cardClasses}>
                            <div
                                className={clsx(
                                    'flex items-center gap-spacing-xs border-solid py-spacing-lg pl-spacing-lg pr-spacing-2xl',
                                    selected
                                        ? 'border-b-2 border-border-brand'
                                        : 'border-b border-border-secondary',
                                )}>
                                <div className="flex min-w-0 flex-1 items-center gap-spacing-lg">
                                    {leading}
                                    <label
                                        htmlFor={itemId}
                                        className={clsx(
                                            'font-medium text-text-secondary',
                                            labelCursor,
                                            sizeClasses.cardTitle,
                                        )}>
                                        {item.title}
                                    </label>
                                </div>
                                <div className="flex shrink-0 items-center">{control}</div>
                            </div>
                            <label
                                htmlFor={itemId}
                                className={clsx(
                                    'relative flex flex-col gap-spacing-xs p-spacing-xl',
                                    labelCursor,
                                )}>
                                {item.subtext && (
                                    <span
                                        className={clsx(
                                            'flex items-baseline gap-spacing-xs font-semibold text-text-secondary',
                                            sizeClasses.price,
                                            '[&_small]:font-regular [&_small]:text-text-tertiary',
                                        )}>
                                        {item.subtext}
                                    </span>
                                )}
                                {item.supportingText && (
                                    <span
                                        className={clsx(
                                            'block font-regular text-text-tertiary',
                                            sizeClasses.periodBody,
                                        )}>
                                        {item.supportingText}
                                    </span>
                                )}
                                {item.badge && (
                                    <span className="absolute right-4 top-4">
                                        {renderBadge(item.badge)}
                                    </span>
                                )}
                            </label>
                        </div>
                    )
                }

                const textColumn = (
                    <div
                        className={clsx(
                            'flex min-w-0 flex-1 flex-col',
                            item.actions && 'gap-spacing-md',
                        )}>
                        <label
                            htmlFor={itemId}
                            className={clsx('flex flex-col', labelCursor, sizeClasses.textColumn)}>
                            <span
                                className={clsx(
                                    'flex flex-wrap items-center',
                                    sizeClasses.titleRow,
                                )}>
                                <span
                                    className={clsx(
                                        'font-medium text-text-secondary',
                                        sizeClasses.text,
                                    )}>
                                    {item.title}
                                </span>
                                {item.subtext && (
                                    <span
                                        className={clsx(
                                            'font-regular text-text-tertiary',
                                            sizeClasses.text,
                                        )}>
                                        {item.subtext}
                                    </span>
                                )}
                                {item.badge && renderBadge(item.badge)}
                            </span>
                            {item.supportingText && (
                                <span
                                    className={clsx(
                                        'font-regular text-text-tertiary',
                                        sizeClasses.text,
                                    )}>
                                    {item.supportingText}
                                </span>
                            )}
                        </label>
                        {item.actions && (
                            <div className="flex items-center gap-spacing-lg text-sm font-semibold">
                                {item.actions}
                            </div>
                        )}
                    </div>
                )

                if (typeConfig.layout === 'trailing-control') {
                    return (
                        <div key={item.value} className={cardClasses}>
                            <div className="flex min-w-0 flex-1 items-start gap-spacing-lg">
                                {leading}
                                {textColumn}
                            </div>
                            <div className="flex shrink-0 items-center">{control}</div>
                        </div>
                    )
                }

                return (
                    <div key={item.value} className={cardClasses}>
                        <div
                            className={clsx(
                                'flex min-w-0 flex-1 items-start',
                                sizeClasses.contentGap,
                            )}>
                            <div className="flex shrink-0 items-center pt-spacing-xxs">
                                {control}
                            </div>
                            {textColumn}
                        </div>
                    </div>
                )
            })}
        </WrapperComponent>
    )
}
